import {CAMPAIGN_WIDGET_TOOL_NAMES} from '../utils/campaignCompletionMessages';
import {isAwaitingBatchEditInstruction} from './campaignStateSelectors';
import {getProcessingToolPart} from './toolPartSelectors';

const APPROVAL_VARIANT_MAP = {
  [CAMPAIGN_WIDGET_TOOL_NAMES.APROVACAO_ESTRATEGIA]: 'strategy',
  [CAMPAIGN_WIDGET_TOOL_NAMES.APROVACAO_ARTES]: 'arts',
  [CAMPAIGN_WIDGET_TOOL_NAMES.MODO_CONCLUSAO]: 'completionMode',
  [CAMPAIGN_WIDGET_TOOL_NAMES.CONFIRMAR_EDICAO_LOTE]: 'batchConfirm',
  [CAMPAIGN_WIDGET_TOOL_NAMES.CONFIRMAR_REJEICAO_LOTE]: 'batchRejectConfirm',
  [CAMPAIGN_WIDGET_TOOL_NAMES.ACOES_FINAIS]: 'finalActions',
};

const ART_ASYNC_TOOL_NAMES = new Set([
  'gerar_artes_campanha',
  'regerar_artes_campanha',
  'editar_arte_campanha',
  'editar_artes_lote',
]);

const ARTS_APPROVAL_TOOL_TYPE = `tool-${CAMPAIGN_WIDGET_TOOL_NAMES.APROVACAO_ARTES}`;

export function isArtApprovalBlockedByProcessing(messages, operation = null) {
  if (operation?.family === 'arts' && operation?.status === 'processing') {
    return true;
  }

  const processingPart = getProcessingToolPart(messages);
  if (!processingPart?.type?.startsWith('tool-')) {
    return false;
  }

  const toolName = processingPart.type.slice('tool-'.length);
  return ART_ASYNC_TOOL_NAMES.has(toolName);
}

function matchesApprovalTrackingId(part, toolCallId) {
  if (!part || !toolCallId) {
    return false;
  }

  return (
    part.toolCallId === toolCallId || part.output?.trackingId === toolCallId
  );
}

export function findLatestArtsApprovalPart(messages) {
  if (!Array.isArray(messages)) {
    return null;
  }

  let latest = null;
  let latestOrdinal = -1;

  messages.forEach((msg, messageIndex) => {
    if (msg?.role !== 'assistant') {
      return;
    }

    (msg.parts || []).forEach((part, partIndex) => {
      if (part.type !== ARTS_APPROVAL_TOOL_TYPE) {
        return;
      }
      if (part.state !== 'output-available') {
        return;
      }

      const ordinal = messageIndex * 10000 + partIndex;
      if (ordinal > latestOrdinal) {
        latestOrdinal = ordinal;
        latest = part;
      }
    });
  });

  return latest;
}

function isArtsApprovalStillActionable(messages, approval) {
  const latestArtsApproval = findLatestArtsApprovalPart(messages);
  if (!latestArtsApproval) {
    return false;
  }

  if (!matchesApprovalTrackingId(latestArtsApproval, approval.toolCallId)) {
    return false;
  }

  if (latestArtsApproval.output?.status !== 'awaiting_selection') {
    return false;
  }

  if (isAwaitingBatchEditInstruction(messages)) {
    return false;
  }

  return true;
}

function findLatestAwaitingApproval(messages, pendingSubmissions = {}) {
  if (!Array.isArray(messages)) {
    return null;
  }

  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (!msg || msg.role !== 'assistant') {
      continue;
    }

    const parts = msg.parts || [];
    for (let j = parts.length - 1; j >= 0; j -= 1) {
      const part = parts[j];
      if (!part || !part.type || !part.type.startsWith('tool-')) {
        continue;
      }

      const toolName = part.type.slice(5);
      if (!APPROVAL_VARIANT_MAP[toolName]) {
        continue;
      }

      if (part.state !== 'output-available') {
        continue;
      }

      if (part.output?.status !== 'awaiting_selection') {
        continue;
      }

      const toolCallId = part.output?.trackingId || part.toolCallId || null;
      if (toolCallId && pendingSubmissions[toolCallId]) {
        continue;
      }

      return {
        variant: APPROVAL_VARIANT_MAP[toolName],
        toolCallId,
      };
    }
  }

  return null;
}

export function getActiveFloatingApproval(messages, options = {}) {
  const pendingSubmissions = options.pendingSubmissions ?? {};
  const approval = findLatestAwaitingApproval(messages, pendingSubmissions);
  if (!approval) {
    return null;
  }

  if (approval.variant === 'arts') {
    if (isArtApprovalBlockedByProcessing(messages, options.operation ?? null)) {
      return null;
    }

    if (!isArtsApprovalStillActionable(messages, approval)) {
      return null;
    }

    if (options.singleArtEditActive) {
      return null;
    }
  }

  return approval;
}
